import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useApi } from "../useApi.js";
import { api } from "../api.js";
import type { PartRow, ServiceCatalogEntry } from "../types.js";
import { Page } from "../components/Shell.js";
import { TableSkeleton } from "../components/Skeleton.js";
import ConfirmDeleteButton from "../components/ConfirmDeleteButton.js";
import { toast } from "../toast.js";

interface PartsResponse { items: PartRow[] }

/** "12,50" oder "12.50" → 12.5; alles andere → null. */
function parseEuro(input: string): number | null {
  const n = Number(input.trim().replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Ein Artikel-/Leistungspreis mit Inline-Bearbeitung: Eingabe, Speichern, Abbrechen. */
function PriceRow({ label, sub, priceCents, onSave, leading, onDelete }: {
  label: string; sub: string; priceCents: number; onSave: (cents: number) => Promise<void>;
  leading?: React.ReactNode; onDelete?: () => Promise<void>;
}) {
  const [value, setValue] = useState((priceCents / 100).toFixed(2).replace(".", ","));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const parsed = parseEuro(value);
  const dirty = parsed !== null && Math.round(parsed * 100) !== priceCents;

  async function save() {
    if (parsed === null) { setErr("Ungültiger Betrag"); return; }
    setBusy(true);
    setErr(null);
    try {
      await onSave(Math.round(parsed * 100));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setValue((priceCents / 100).toFixed(2).replace(".", ","));
    setErr(null);
  }

  return (
    <tr>
      {leading && <td>{leading}</td>}
      <td><b>{label}</b><div className="sub-t">{sub}</div></td>
      <td className="r">
        <div style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <input
            className="mono" inputMode="decimal" value={value}
            onChange={(e) => setValue(e.target.value)}
            style={{
              width: 92, textAlign: "right", padding: "6px 9px", fontSize: 13,
              background: "var(--surface)", border: "1px solid var(--line-2)",
              borderRadius: "var(--r-sm)",
            }}
          />
          <span className="sub-t">€</span>
        </div>
        {err && <div className="err" style={{ marginTop: 8, marginBottom: 0 }}>{err}</div>}
      </td>
      <td className="r" style={{ whiteSpace: "nowrap" }}>
        {dirty ? (
          <span style={{ display: "inline-flex", gap: 6 }}>
            <button className="btn ghost sm" disabled={busy} onClick={cancel}>Abbrechen</button>
            <button className="btn pri sm" disabled={busy} onClick={() => void save()}>
              {busy ? "Speichert…" : "Speichern"}
            </button>
          </span>
        ) : onDelete && <ConfirmDeleteButton onConfirm={onDelete} />}
      </td>
    </tr>
  );
}

/** Vorschaubild mit Upload/Ersetzen-Knopf – Datei geht direkt an /api/parts/:id/image. */
function ImageCell({ partId, imagePath, onUploaded }: {
  partId: string; imagePath: string | null; onUploaded: () => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function pick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setErr(null);
    try {
      await api.upload(`/api/parts/${partId}/image`, file);
      await onUploaded();
    } catch (err) {
      setErr(err instanceof Error ? err.message : "Upload fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {imagePath
        ? <img src={imagePath} alt="" style={{
            width: 34, height: 34, borderRadius: 6, objectFit: "cover",
            border: "1px solid var(--line)", flex: "none",
          }} />
        : <div style={{
            width: 34, height: 34, borderRadius: 6, background: "var(--surface-2)",
            border: "1px dashed var(--line-2)", flex: "none",
          }} />}
      <button className="btn ghost sm" disabled={busy} onClick={() => inputRef.current?.click()}>
        {busy ? "…" : imagePath ? "Ersetzen" : "Hochladen"}
      </button>
      <input ref={inputRef} type="file" hidden
             accept="image/png,image/jpeg,image/webp,image/svg+xml"
             onChange={(e) => void pick(e)} />
      {err && <div className="err" style={{ fontSize: 11, padding: "2px 6px" }}>{err}</div>}
    </div>
  );
}

const emptyForm = {
  sku: "", name: "", group: "", scope: "ANY" as "ANY" | "SNR7" | "SNR12",
  isWearPart: false, unit: "METER" as "METER" | "HOUR",
  lifeValue: "", packSize: "1", priceCents: "", stock: "0", minStock: "0", monthlyUse: "0",
};

/** Neuen Artikel im Stamm anlegen. */
function NewPartForm({ onCreated }: { onCreated: () => Promise<void> }) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const price = parseEuro(form.priceCents);
    if (price === null) { setErr("Ungültiger Preis"); return; }

    setBusy(true);
    setErr(null);
    try {
      await api.post("/api/parts", {
        sku: form.sku, name: form.name, group: form.group, scope: form.scope,
        isWearPart: form.isWearPart, unit: form.unit,
        lifeValue: form.isWearPart && form.lifeValue ? Number(form.lifeValue) : null,
        packSize: Number(form.packSize) || 1,
        priceCents: Math.round(price * 100),
        stock: Number(form.stock) || 0,
        minStock: Number(form.minStock) || 0,
        monthlyUse: Number(form.monthlyUse) || 0,
      });
      setForm(emptyForm);
      await onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Anlegen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      {err && <div className="err" role="alert">{err}</div>}
      <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: "10px 14px" }}>
        <div className="field">
          <label htmlFor="p-sku">Positionsnummer</label>
          <input id="p-sku" required value={form.sku} onChange={(e) => set("sku", e.target.value)} />
        </div>
        <div className="field" style={{ gridColumn: "span 2" }}>
          <label htmlFor="p-name">Bezeichnung</label>
          <input id="p-name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-group">Gruppe</label>
          <input id="p-group" required placeholder="z. B. Komponenten"
                 value={form.group} onChange={(e) => set("group", e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-scope">Rohrdimension</label>
          <select id="p-scope" value={form.scope}
                  onChange={(e) => set("scope", e.target.value as typeof form.scope)}
                  style={{ padding: "9px 11px", background: "var(--surface-2)",
                           border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
            <option value="ANY">Beide (ANY)</option>
            <option value="SNR7">SNR 7 × 1,5</option>
            <option value="SNR12">SNR 12 × 2,0</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="p-unit">Einheit</label>
          <select id="p-unit" value={form.unit}
                  onChange={(e) => set("unit", e.target.value as typeof form.unit)}
                  style={{ padding: "9px 11px", background: "var(--surface-2)",
                           border: "1px solid var(--line-2)", borderRadius: "var(--r-sm)" }}>
            <option value="METER">Meter</option>
            <option value="HOUR">Betriebsstunden</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="p-price">Preis (€)</label>
          <input id="p-price" required inputMode="decimal" placeholder="z. B. 12,50"
                 value={form.priceCents} onChange={(e) => set("priceCents", e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-pack">Verpackungseinheit</label>
          <input id="p-pack" type="number" min={1} value={form.packSize}
                 onChange={(e) => set("packSize", e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-stock">Bestand</label>
          <input id="p-stock" type="number" min={0} value={form.stock}
                 onChange={(e) => set("stock", e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-min">Mindestbestand</label>
          <input id="p-min" type="number" min={0} value={form.minStock}
                 onChange={(e) => set("minStock", e.target.value)} />
        </div>
        <div className="field" style={{ justifyContent: "center" }}>
          <label htmlFor="p-wear" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input id="p-wear" type="checkbox" style={{ width: "auto" }}
                   checked={form.isWearPart} onChange={(e) => set("isWearPart", e.target.checked)} />
            Verschleißteil
          </label>
        </div>
        {form.isWearPart && (
          <div className="field">
            <label htmlFor="p-life">Wechselintervall</label>
            <input id="p-life" type="number" min={1} placeholder={form.unit === "HOUR" ? "Bh" : "m"}
                   value={form.lifeValue} onChange={(e) => set("lifeValue", e.target.value)} />
          </div>
        )}
      </div>
      <button className="btn pri" type="submit" disabled={busy} style={{ marginTop: 14 }}>
        {busy ? "Wird angelegt…" : "Artikel anlegen"}
      </button>
    </form>
  );
}

export default function Prices() {
  const partsApi = useApi<PartsResponse>("/api/parts");
  const catalogApi = useApi<ServiceCatalogEntry[]>("/api/service/catalog");
  const [open, setOpen] = useState(false);

  async function savePartPrice(id: string, priceCents: number) {
    await api.patch(`/api/parts/${id}/price`, { priceCents });
    await partsApi.reload();
    toast("Preis gespeichert", "success");
  }
  async function saveServicePrice(code: string, priceCents: number) {
    await api.patch(`/api/service/catalog/${code}`, { priceCents });
    await catalogApi.reload();
    toast("Preis gespeichert", "success");
  }
  async function deletePart(id: string) {
    try {
      await api.delete(`/api/parts/${id}`);
      await partsApi.reload();
      toast("Artikel gelöscht", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Löschen fehlgeschlagen", "error");
    }
  }

  return (
    <Page title="Preise" sub="Ersatzteile und Service-Leistungen – Änderungen landen in der Historie"
          actions={<button className="btn pri" onClick={() => setOpen((o) => !o)}>
            {open ? "Abbrechen" : "Ersatzteil anlegen"}
          </button>}>
      <div className="grid" style={{ gap: 16 }}>
        {open && (
          <div className="card">
            <div className="card-h"><h3>Neues Ersatzteil</h3></div>
            <div className="card-b">
              <NewPartForm onCreated={async () => {
                setOpen(false); await partsApi.reload(); toast("Artikel angelegt", "success");
              }} />
            </div>
          </div>
        )}

        <div className="card">
          <div className="card-h">
            <h3>Ersatzteile</h3>
            <span className="hint">Preis und Bild je Artikel – wirkt sofort auf Bestellvorschlag und neue Bestellungen</span>
          </div>
          {partsApi.loading && <TableSkeleton widths={[10, 35, 15]} />}
          {partsApi.error && <div className="empty">{partsApi.error}</div>}
          {partsApi.data && (
            <div className="tbl-wrap"><table>
              <thead><tr><th>Bild</th><th>Artikel</th><th className="r">Preis</th><th /></tr></thead>
              <tbody>
                {partsApi.data.items.map((p) => (
                  <PriceRow key={p.id} label={p.name} sub={`Pos. ${p.sku} · ${p.group}`}
                            priceCents={p.priceCents}
                            onSave={(cents) => savePartPrice(p.id, cents)}
                            onDelete={() => deletePart(p.id)}
                            leading={<ImageCell partId={p.id} imagePath={p.imagePath}
                                                 onUploaded={async () => {
                                                   await partsApi.reload(); toast("Bild hochgeladen", "success");
                                                 }} />} />
                ))}
              </tbody>
            </table></div>
          )}
        </div>

        <div className="card">
          <div className="card-h">
            <h3>Service-Leistungen</h3>
            <span className="hint">Leistungspositionen A44–A49 aus dem Rahmenvertrag</span>
          </div>
          {catalogApi.loading && <TableSkeleton widths={[40, 15]} rows={4} />}
          {catalogApi.error && <div className="empty">{catalogApi.error}</div>}
          {catalogApi.data && (
            <div className="tbl-wrap"><table>
              <thead><tr><th>Leistung</th><th className="r">Preis</th><th /></tr></thead>
              <tbody>
                {catalogApi.data.map((s) => (
                  <PriceRow key={s.code} label={s.name}
                            sub={`Pos. ${s.code}${s.loaner ? " · mit Leihgerät" : ""}`}
                            priceCents={s.priceCents}
                            onSave={(cents) => saveServicePrice(s.code, cents)} />
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>
    </Page>
  );
}
